import type {
  Annotation,
  AnnotationShape,
  AnnotationStatus,
  ArtifactPreview,
} from "@rakazo/contracts";
import { rpc } from "../../lib/rpc";

/** What the client sends along with one annotation on "Umsetzen": PNG of the area and its text. */
export type MarkupExcerpt = { annotationId: string; png: string; text: string };

/**
 * Everything the markup view asks of the server, as one object so tests can swap in a mock.
 * Every method mirrors one procedure of artifacts.preview* and annotations.* in the contract.
 */
export type MarkupClient = {
  preview: (artifactId: string, signal?: AbortSignal) => Promise<ArtifactPreview>;
  previewPdf: (artifactId: string, signal?: AbortSignal) => Promise<Uint8Array>;
  list: (artifactId: string, signal?: AbortSignal) => Promise<Annotation[]>;
  create: (input: {
    artifactId: string;
    page: number;
    shape: AnnotationShape;
    comment: string;
  }) => Promise<Annotation>;
  update: (input: {
    id: string;
    shape?: AnnotationShape;
    comment?: string;
    status?: AnnotationStatus;
  }) => Promise<Annotation>;
  remove: (id: string) => Promise<void>;
  submit: (input: {
    artifactId: string;
    annotationIds: string[];
    excerpts: MarkupExcerpt[];
  }) => Promise<{ botId: string; threadId: string; messageId: string }>;
};

/** The markup client backed by the app's rpc connection. */
export const rpcMarkupClient: MarkupClient = {
  preview: (artifactId, signal) => rpc.artifacts.preview({ artifactId }, { signal }),
  previewPdf: async (artifactId, signal) => {
    const blob = await rpc.artifacts.previewPdf({ artifactId }, { signal });
    return new Uint8Array(await blob.arrayBuffer());
  },
  list: (artifactId, signal) => rpc.annotations.list({ artifactId }, { signal }),
  create: (input) => rpc.annotations.create(input),
  update: (input) => rpc.annotations.update(input),
  remove: async (id) => {
    await rpc.annotations.remove({ id });
  },
  submit: (input) => rpc.annotations.submit(input),
};
